import { Directive, Input, OnDestroy, QueryList, ViewChildren } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { SafeResourceUrl } from '@angular/platform-browser';
import { Parser } from 'json2csv';
import { MenuItem, MessageService } from 'primeng/api';
import { FileUpload } from 'primeng/fileupload';
import { Menu } from 'primeng/menu';
import { Table } from 'primeng/table';
import { TieredMenu } from 'primeng/tieredmenu';
import { BehaviorSubject, from, Subject, Subscription } from 'rxjs';
import { concatMap, debounceTime, finalize, first, takeUntil, tap } from 'rxjs/operators';
import { changeDetection } from '../../change-detection';
import { Service } from '../../classes/service';
import { FormGenerator } from '../../forms/form-generator';
import { ModalService } from '../../services/modal.service';
import { ServiceProviderService } from '../../services/service-provider.service';
import { stringToKey } from '../../string-to-key';
import { UniqueId } from '../../uniqueId';
import { DynamicFormStructure } from '../dynamic-form-structure';
import { DynamicModel } from '../dynamic-model';
import csvToJson from 'csvtojson';

import _ from 'lodash';
import { FormStructure } from '../../forms/form-structure';
import { SiteService } from '../../services/site.service';

@Directive()
export class GenericServiceProvider implements OnDestroy {
  @ViewChildren('fileUploadElement') fileUploadElements!: QueryList<FileUpload>;
  @ViewChildren('table') tableElements!: QueryList<Table>;

  @Input() data!: DynamicModel;
  @Input() serviceIndex!: number;

  fileUpload!: FileUpload;
  table!: Table;

  csvExportUrl!: SafeResourceUrl;

  additionalOperations: MenuItem[];
  modifyCategories: MenuItem[];

  service!: Service;

  formReady: BehaviorSubject<boolean>;

  products: BehaviorSubject<any[]>;
  categories: BehaviorSubject<any[]>;

  clonedProducts: { [s: string]: any } = {};
  clonedCategories: { [s: string]: any } = {};

  activeProductIndex?: number;
  activeCategoryIndex?: number;

  subscriptions: Subscription[];

  // Guards against concurrent whole-provider saves triggered by inline edits on
  // a not-yet-persisted service (see saveWholeProviderForUnpersistedService).
  private isSavingWholeProvider = false;

  private unsubscribe$ = new Subject<void>();

  constructor(
    private siteService: SiteService,
    private modalService: ModalService,
    private messageService: MessageService,
    private serviceProviderService: ServiceProviderService
  ) {
    this.formReady = new BehaviorSubject<boolean>(false);
    this.products = new BehaviorSubject<any[]>([]);
    this.categories = new BehaviorSubject<any[]>([]);

    this.additionalOperations = [
      { label: 'Description', icon: 'pi pi-fw pi-plus', command: () => this.onEditDescription() },
      { label: 'Notes', icon: 'pi pi-fw pi-plus', command: () => this.onEditNotes() },
      {
        label: 'Invoice Label',
        icon: 'pi pi-fw pi-plus',
        command: () => this.onEditInvoiceLabel(),
      },
      { label: 'View Documents', icon: 'pi pi-fw pi-folder-open', command: () => this.onViewProductMedia('document') },
      { label: 'View Images', icon: 'pi pi-fw pi-images', command: () => this.onViewProductMedia('image') },
      { label: 'Upload Documents', icon: 'pi pi-fw pi-file', command: () => this.onUploadProductMedia('document') },
      { label: 'Upload Images', icon: 'pi pi-fw pi-image', command: () => this.onUploadProductMedia('image') },
    ];

    this.modifyCategories = [
      { label: 'New', icon: 'pi pi-fw pi-plus', command: () => this.onEditCategory(null) },
      { label: 'Edit', icon: 'pi pi-fw pi-pencil', items: [] },
      { label: 'Delete', icon: 'pi pi-fw pi-trash', styleClass: 'menu-item-delete', items: [] },
      { label: 'Restore', icon: 'pi pi-fw pi-refresh', styleClass: 'menu-item-restore', items: [] },
    ];

    this.subscriptions = [];

    this.siteService.addSubscriptionLog(
      this,
      'generic-service-provider.ts->constructor->this.subscriptions.push(this.categories'
    );

    this.subscriptions.push(
      this.categories
        .pipe(
          finalize(() =>
            this.siteService.setSubscriptionLogFinalised(
              'generic-service-provider.ts->constructor->this.subscriptions.push(this.categories'
            )
          ),
          debounceTime(100)
        )
        .subscribe({
          next: (categories: any[]) => {
            let editMenuItems: MenuItem[] = [];
            let deleteMenuItems: MenuItem[] = [];
            let restoreMenuItems: MenuItem[] = [];

            _.map(categories, (category: any, index: number) => {
              editMenuItems.push({
                label: category.name,
                command: () => this.onEditCategory(index),
              });

              if (category.disabled) {
                restoreMenuItems.push({
                  label: category.name,
                  command: () => this.onRestoreCategory(index),
                });
              } else {
                deleteMenuItems.push({
                  label: category.name,
                  command: () => this.onDeleteCategory(index),
                });
              }
            });

            this.modifyCategories[1].items = editMenuItems;

            const deleteIndex = _.findIndex(
              this.modifyCategories,
              (mc) => mc.label?.toLowerCase() === 'delete'
            );

            if (deleteMenuItems.length) {
              this.modifyCategories[deleteIndex].visible = true;
              this.modifyCategories[deleteIndex].items = deleteMenuItems;
            } else {
              this.modifyCategories[deleteIndex].visible = false;
            }

            const restoreIndex = _.findIndex(
              this.modifyCategories,
              (mc) => mc.label?.toLowerCase() === 'restore'
            );

            if (restoreMenuItems.length) {
              this.modifyCategories[restoreIndex].visible = true;
              this.modifyCategories[restoreIndex].items = restoreMenuItems;
            } else {
              this.modifyCategories[restoreIndex].visible = false;
            }
          },
        })
    );
  }

  ngOnInit(formStructure: FormStructure): void {
    this.service = this.data.service;

    this.generateForm(formStructure);

    // The "Delete multiple" toolbar lives in the provider form (next to the
    // service-type media buttons); it drives this table through the shared
    // Service instance rather than a view reference (#215).
    this.service.productSelectionCommand$.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (command) => {
        if (command === 'enter') {
          this.enterSelectionMode();
        } else if (command === 'exit') {
          this.exitSelectionMode();
        } else if (command === 'confirm') {
          this.confirmDeleteSelected();
        }
      },
    });
  }

  ngAfterViewInit(): void {
    this.siteService.addSubscriptionLog(
      this,
      'generic-service-provider.ts->ngAfterViewInit->this.fileUploadElements.changes'
    );

    this.fileUploadElements.changes
      .pipe(
        finalize(() =>
          this.siteService.setSubscriptionLogFinalised(
            'generic-service-provider.ts->ngAfterViewInit->this.fileUploadElements.changes'
          )
        ),
        first((queryList: QueryList<FileUpload>) => queryList.length > 0)
      )
      .subscribe({
        next: (queryList: QueryList<FileUpload>) => {
          this.fileUpload = queryList.first;
        },
      });

    this.siteService.addSubscriptionLog(
      this,
      'generic-service-provider.ts->ngAfterViewInit->this.tableElements.changes'
    );

    this.tableElements.changes
      .pipe(
        finalize(() =>
          this.siteService.setSubscriptionLogFinalised(
            'generic-service-provider.ts->ngAfterViewInit->this.tableElements.changes'
          )
        ),
        first((queryList: QueryList<Table>) => queryList.length > 0)
      )
      .subscribe({
        next: (queryList: QueryList<Table>) => {
          this.table = queryList.first;
        },
      });

    changeDetection(() => {
      this.formReady.next(true);
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      if (subscription) {
        subscription.unsubscribe();
      }
    });

    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  onExportCSV(event: Event) {
    const products = this.service.form.get('products')?.value;

    const data = _.map(products, (product) => {
      return {
        id: product._meta.id,
        name: product.name,
        category: product.category.name,
        cost: product.cost.amount,
        bespoke: product.cost.bespoke ? 1 : 0,
        description: product.description,
        note: product.note,
      };
    });

    const fileName = stringToKey(this.service.name) + '.csv';

    const json2csvParser = new Parser();

    const csv = json2csvParser.parse(data);

    let file = new File([csv], fileName, { type: 'text/csv' });

    let exportUrl = URL.createObjectURL(file);

    window.location.assign(exportUrl);

    // URL.revokeObjectURL(exportUrl);
  }

  onAddCategory(event: Event | null, index?: number): void {
    const formGroup = this.generateProductCategoryFormGroup();

    const categoriesFormArray = this.service.form.get('productCategories') as FormArray;

    if (categoriesFormArray) {
      if (index !== undefined) {
        categoriesFormArray.insert(index, formGroup);
      } else {
        categoriesFormArray.push(formGroup);
      }

      this.categories.next(this.formatProductCategoriesForSelectMenu(categoriesFormArray.value));
    }
  }

  onAddProduct(event: Event | null, index?: number): void {
    const uniqueId = new UniqueId();
    const formGenerator = new FormGenerator();
    const dynamicFormStructure = new DynamicFormStructure();

    const id = uniqueId.getUniqueInArray(this.products.value, '_meta.id', 'id-');
    // Advisory only — the server assigns max(sibling index) + 1 when it first
    // persists a new product. `products.value.length` was wrong whenever a
    // product was hidden or a delete was unsaved, and minted duplicate indexes
    // that hid products on the next load (#215).
    const metaIndex = this.nextProductIndex();

    const formStructure = dynamicFormStructure._getGenericProductStructure();

    const formGroup = formGenerator.generate(formStructure, {
      _meta: { id, index: metaIndex },
      name: 'N/A',
      category: this.categories.value[0].value,
      cost: { amount: 0 },
    });

    const productsFormArray = this.service.form.get('products') as FormArray;

    if (productsFormArray) {
      if (index !== undefined) {
        productsFormArray.insert(index, formGroup);

        this.products.next(productsFormArray.value);
      } else {
        // productsFormArray.push(formGroup);
      }
    }
  }

  /**
   * Trash icon. Since #215 the removal is persisted on Confirm (a per-product
   * server delete), mirroring how adds and edits already persist on blur. It
   * used to only `removeAt` in the form, so a deletion reached the DB solely
   * via the next whole-provider Save — which then carried every accumulated
   * delete, tripped the server's 300-path circuit breaker on a real catalogue
   * clean-up, and lost the whole session's removals (Killara QLD, Sep 2026).
   */
  onDeleteProduct(event: Event, product: any, index: number) {
    const modalRef = this.modalService.generic({
      title: 'Remove Item',
      copy: [
        'Are you sure you want to remove this item?',
        'It is removed immediately — you do not need to press Save.',
      ],
      buttons: [
        { label: 'Cancel', key: 'cancel', class: '' },
        { label: 'Confirm', key: 'confirm', class: 'p-button-danger', icon: 'pi pi-trash' },
      ],
    });

    modalRef.onClose.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (reason: string) => {
        if (reason.toString().toLowerCase() !== 'confirm') {
          return;
        }

        const productId: string | undefined = _.get(product, '_meta.id');

        // A service that isn't on the server yet has nothing to delete there:
        // drop the row and persist via the whole-provider save (same fallback
        // the inline edits use).
        if (!this.isCurrentServicePersisted() || !productId) {
          const productsFormArray = this.service.form.get('products') as FormArray;

          if (productsFormArray) {
            productsFormArray.removeAt(index);

            this.products.next(productsFormArray.value);
          }

          if (this.service.serviceProviderId && !this.isSavingWholeProvider) {
            this.isSavingWholeProvider = true;

            this.service.serviceProvider.save().subscribe({
              next: () => {
                this.isSavingWholeProvider = false;
              },
              error: (err) => {
                this.isSavingWholeProvider = false;
                this.modalService.error(err?.error?.error ?? err);
              },
            });
          }

          return;
        }

        this.deleteProductsOnServer([productId]);
      },
    });
  }

  // --- Multi-select removal (#215) -------------------------------------------
  // State is on the shared Service instance (see Service.productSelection) so
  // the provider form's toolbar can render counts/buttons without a view ref.

  /** Products per server request. Server ceiling is 50; 25 keeps each call small. */
  static readonly DELETE_CHUNK_SIZE = 25;

  get selectionMode(): boolean {
    return !!this.service?.productSelection.active;
  }

  get selectedCount(): number {
    return this.service?.productSelection.selected.size ?? 0;
  }

  get allSelected(): boolean {
    const products: any[] = this.products.value || [];

    return (
      products.length > 0 &&
      products.every((product) => this.isSelected(product))
    );
  }

  isSelected(product: any): boolean {
    const id = _.get(product, '_meta.id');

    return !!id && this.service.productSelection.selected.has(id);
  }

  toggleSelected(product: any, checked?: boolean): void {
    const id = _.get(product, '_meta.id');

    if (!id) {
      return;
    }

    const selected = this.service.productSelection.selected;
    const shouldSelect = checked === undefined ? !selected.has(id) : checked;

    if (shouldSelect) {
      selected.add(id);
    } else {
      selected.delete(id);
    }
  }

  toggleSelectAll(checked: boolean): void {
    const selected = this.service.productSelection.selected;

    selected.clear();

    if (checked) {
      for (const product of this.products.value || []) {
        const id = _.get(product, '_meta.id');

        if (id) {
          selected.add(id);
        }
      }
    }
  }

  enterSelectionMode(): void {
    if (!this.isCurrentServicePersisted()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Save the provider first',
        detail: 'Items can be removed in bulk once this service has been saved.',
      });
      return;
    }

    this.service.productSelection.selected.clear();
    this.service.productSelection.active = true;
  }

  exitSelectionMode(): void {
    if (this.service.productSelection.busy) {
      return;
    }

    this.service.productSelection.selected.clear();
    this.service.productSelection.active = false;
  }

  confirmDeleteSelected(): void {
    const ids = Array.from(this.service.productSelection.selected);

    if (!ids.length || this.service.productSelection.busy) {
      return;
    }

    const noun = ids.length === 1 ? 'item' : 'items';

    const modalRef = this.modalService.generic({
      title: 'Remove Items',
      copy: [
        `Are you sure you want to remove ${ids.length} ${noun}?`,
        'They are removed immediately — you do not need to press Save.',
      ],
      buttons: [
        { label: 'Cancel', key: 'cancel', class: '' },
        {
          label: `Remove ${ids.length} ${noun}`,
          key: 'confirm',
          class: 'p-button-danger',
          icon: 'pi pi-trash',
        },
      ],
    });

    modalRef.onClose.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (reason: string) => {
        if (reason && reason.toString().toLowerCase() === 'confirm') {
          this.deleteProductsOnServer(ids);
        }
      },
    });
  }

  /**
   * Delete the given products on the server in sequential chunks (one version
   * bump per chunk — parallel calls would race the unguarded version row),
   * then drop them from the form. Ids the server reports as already gone are
   * dropped too. On a mid-way failure the chunks that did succeed are still
   * removed locally, so a retry only re-sends what is left.
   */
  private deleteProductsOnServer(ids: string[]): void {
    const state = this.service.productSelection;
    const chunks = _.chunk(_.uniq(ids), GenericServiceProvider.DELETE_CHUNK_SIZE);
    const gone: string[] = [];

    state.busy = true;

    from(chunks)
      .pipe(
        concatMap((chunk) =>
          this.service.deleteProducts(chunk).pipe(
            tap((res) => {
              gone.push(...(res?.deleted ?? []), ...(res?.notFound ?? []));
            })
          )
        ),
        takeUntil(this.unsubscribe$)
      )
      .subscribe({
        complete: () => {
          this.removeProductsFromForm(gone);

          state.busy = false;
          state.selected.clear();
          state.active = false;

          const noun = gone.length === 1 ? 'item has' : 'items have';

          this.messageService.add({
            severity: 'success',
            summary: 'Removed',
            detail: `${gone.length} ${noun} been removed.`,
          });
        },
        error: (err) => {
          this.removeProductsFromForm(gone);

          state.busy = false;

          for (const id of gone) {
            state.selected.delete(id);
          }

          this.modalService.error(err?.error?.error ?? err);
        },
      });
  }

  private removeProductsFromForm(ids: string[]): void {
    if (!ids.length) {
      return;
    }

    const productsFormArray = this.service.form.get('products') as FormArray;

    if (!productsFormArray) {
      return;
    }

    const idSet = new Set(ids);

    for (let i = productsFormArray.length - 1; i >= 0; i--) {
      const id = _.get(productsFormArray.at(i).value, '_meta.id');

      if (id && idSet.has(id)) {
        productsFormArray.removeAt(i);
        delete this.clonedProducts[id];
      }
    }

    this.products.next(productsFormArray.value);
  }

  /** max(_meta.index) + 1 over the products currently in the form (0 when empty). */
  private nextProductIndex(): number {
    let maxIndex = -1;

    for (const product of this.products.value || []) {
      const index = Number(_.get(product, '_meta.index'));

      if (Number.isFinite(index) && index > maxIndex) {
        maxIndex = index;
      }
    }

    return maxIndex + 1;
  }

  onChangeInput(event: any, product: any): void {
    // Only flag as changed if the service provider exists in the DB and has an ID
    if (this.service.serviceProviderId) {
      console.log(
        'onChangeInput: ',
        event,
        product,
        this.service,
        this.serviceIndex,
        this.clonedProducts[product._meta.id]
      );

      _.set(product, '_meta.hasChanged', true);
    }
  }

  onDropdownKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      event.stopImmediatePropagation();
      event.stopPropagation();
      console.log(`Prevented default behavior for ${event.key}`);
    }
  }

  onKeyDownConfirm(event: any): void {
    event.target?.blur();
  }

  onKeyDownCancel(event: any, product: any, rowIndex: number, isDropDown = false): void {
    _.set(product, '_meta.hasChanged', false);

    console.log('onKeyDownCancel: ', isDropDown);

    if (isDropDown) {
      const clonedProduct = this.clonedProducts[product._meta.id];

      console.log(clonedProduct);
    }

    this.revertModifiedProduct(product, rowIndex);
  }

  onBlurInput(event: Event, product: any, path: string, rowIndex: number): void {
    // this is so we can show the user nice, human readable, text
    let displayPath: string = '';

    // If we have something like 'cost.amount' make it look better for the user.
    if (path.includes('.')) {
      // Split 'cost.amount' and take the first element so we get something like 'cost'.
      displayPath = path.split('.')[0];
    } else {
      displayPath = path; // Simple paths will already be descriptive enough for the user
    }

    // We only save on blur if we have an existing service provider that is saved in the DB
    // Also, on save if the data has changed since the page loaded
    if (this.service.serviceProviderId && _.get(product, '_meta.hasChanged', false)) {
      // Remove `hasChanged` because we want to detect any future changes
      _.unset(product, '_meta.hasChanged');

      // If this service hasn't been persisted yet (e.g. just added to an
      // existing provider), the server has no service id to PATCH against. Save
      // the whole provider instead — that persists the new service AND this
      // product change together. Subsequent edits then use the fast PATCH path.
      if (!this.isCurrentServicePersisted()) {
        this.saveWholeProviderForUnpersistedService(product, path, displayPath, rowIndex);
        return;
      }

      // Clone so we don't add `saving...` to the DB.
      const clonedData = _.cloneDeep(product);

      // Remove any `saving` flags that might already exist.
      _.unset(clonedData, '_meta.saving');

      // We have to flatten otherwise a path like `cost.amount` won't work
      const savingPath = _.flatten(['_meta', 'saving', path.split('.')]);

      // Set the specific column to "saving" so we can display an indicator to the user
      _.set(product, savingPath, true);

      // Send the data to the server
      this.service.saveProduct(clonedData, this.serviceIndex, path).subscribe({
        next: (res) => {
          // Stop the saving indicator
          _.unset(product, savingPath);

          // Tell the user the process was complete
          this.messageService.add({
            severity: 'success',
            summary: 'Save Complete',
            detail: _.startCase(displayPath) + ' has been saved.',
          });
        },
        error: (err) => {
          // Stop the saving indicator
          _.unset(product, savingPath);

          // Show the user the error message
          this.modalService.error(err.error.error);
        },
      });

      this.updateModifiedProduct(product, rowIndex);
    }
  }

  /** True if the service this component edits already exists server-side. */
  protected isCurrentServicePersisted(): boolean {
    const serviceId = this.service?._meta?.id ?? '';
    return !!this.service?.serviceProvider?.isServicePersisted(serviceId);
  }

  /**
   * Persist a single product field (description/note/invoiceLabel etc). Uses the
   * fast per-product PATCH when the service already exists server-side, and
   * falls back to a whole-provider save for a not-yet-persisted service (the
   * change is already on the product, which lives in the form). One helper so
   * every product-field save handles the unsaved-service case identically.
   */
  protected saveProductField(product: any, path: string): void {
    const onSuccess = () =>
      this.messageService.add({
        severity: 'success',
        summary: 'Save Complete',
        detail: _.startCase(path) + ' has been saved.',
      });
    const onError = (err: any) => this.modalService.error(err?.error?.error ?? err);

    if (this.isCurrentServicePersisted()) {
      this.service.saveProduct(product, this.serviceIndex, path).subscribe({
        next: onSuccess,
        error: onError,
      });
      return;
    }

    if (this.isSavingWholeProvider) {
      return;
    }
    this.isSavingWholeProvider = true;
    this.service.serviceProvider.save().subscribe({
      next: () => {
        this.isSavingWholeProvider = false;
        onSuccess();
      },
      error: (err) => {
        this.isSavingWholeProvider = false;
        onError(err);
      },
    });
  }

  /**
   * Persist an inline product change on a not-yet-saved service by saving the
   * whole provider (the change is already in the form). One save at a time —
   * concurrent blur events while a save is in flight are ignored, and the
   * `saving` indicator on the edited cell is cleared when it settles.
   */
  protected saveWholeProviderForUnpersistedService(
    product: any,
    path: string,
    displayPath: string,
    rowIndex: number
  ): void {
    if (this.isSavingWholeProvider) {
      return;
    }
    this.isSavingWholeProvider = true;

    const savingPath = _.flatten(['_meta', 'saving', path.split('.')]);
    _.set(product, savingPath, true);
    this.updateModifiedProduct(product, rowIndex);

    this.service.serviceProvider.save().subscribe({
      next: () => {
        this.isSavingWholeProvider = false;
        _.unset(product, savingPath);
        this.messageService.add({
          severity: 'success',
          summary: 'Save Complete',
          detail: _.startCase(displayPath) + ' has been saved.',
        });
      },
      error: (err) => {
        this.isSavingWholeProvider = false;
        _.unset(product, savingPath);
        // Re-mark as changed so the next blur retries the save.
        _.set(product, ['_meta', 'hasChanged'], true);
        this.modalService.error(err?.error?.error ?? err);
      },
    });
  }

  onFocusInput(event: FocusEvent, product: any): void {
    // The dropdown triggers the `onFocus` event whenever its value changes.
    // Ensure we don't overwrite the current value unnecessarily.
    if (!this.clonedProducts[product._meta.id]) {
      this.clonedProducts[product._meta.id] = _.cloneDeep(product);
    }

    const inputElement = event.target as HTMLInputElement;

    inputElement.select();
  }

  onRowEditInit(product: any) {
    this.clonedProducts[product._meta.id] = _.cloneDeep(product);
  }

  onRowEditSave(product: any, index: number) {
    this.updateModifiedProduct(product, index);
  }

  onRowEditCancel(product: any, index: number) {
    this.revertModifiedProduct(product, index);
  }

  onDisplayAdditionalOperationsMenu(event: Event, menu: Menu, index: number) {
    this.activeProductIndex = index;

    menu.toggle(event);
  }

  onDisplayModifyCategoriesMenu(event: Event, menu: TieredMenu, index: number) {
    menu.toggle(event);
  }

  // Resolves the active product's media scope: (serviceProviderId, productSubKey) where
  // productSubKey = "<serviceMetaId>:<productMetaId>". Returns null (and toasts) if the
  // provider/service/product isn't saved yet. Shared by upload + view.
  private getActiveProductMediaScope(): { serviceProviderId: number; productSubKey: string } | null {
    const products = this.products.value;
    if (!products?.length || this.activeProductIndex === undefined) return null;

    const product = products[this.activeProductIndex];
    const serviceProviderId = this.service?.serviceProviderId;
    const serviceMetaId = this.service?._meta?.id;
    const productMetaId = _.get(product, '_meta.id');

    if (serviceProviderId == null || !serviceMetaId || !productMetaId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Save product first',
        detail: 'Save this provider/product before attaching or viewing media.',
      });
      return null;
    }
    return { serviceProviderId: Number(serviceProviderId), productSubKey: `${serviceMetaId}:${productMetaId}` };
  }

  // View documents/images for the active catalogue product.
  onViewProductMedia(kind: 'document' | 'image'): void {
    const scope = this.getActiveProductMediaScope();
    if (!scope) return;
    this.serviceProviderService
      .getItemMedia(scope.serviceProviderId, scope.productSubKey, kind)
      .subscribe({
        next: (items) =>
          this.modalService.mediaViewer({
            kind,
            items,
            title: kind === 'image' ? 'Item images' : 'Item documents',
            emptyMessage:
              kind === 'image' ? 'No images uploaded yet.' : 'No documents uploaded yet.',
          }),
        error: () =>
          this.messageService.add({
            severity: 'error',
            summary: 'Could not load media',
            detail: 'Please try again.',
          }),
      });
  }

  // Upload documents/images to the active catalogue product.
  onUploadProductMedia(kind: 'document' | 'image'): void {
    const scope = this.getActiveProductMediaScope();
    if (!scope) return;
    const { serviceProviderId, productSubKey } = scope;

    const ref = this.modalService.fileUpload({
      title: kind === 'image' ? 'Upload images' : 'Upload documents',
      bucketPrefix: kind === 'image' ? 'service-item-images' : 'service-item-documents',
      accept: kind === 'image' ? 'image/*' : '*',
      multiple: true,
    });
    ref.onClose.subscribe((result?: { files: { id: number }[] }) => {
      if (!result?.files?.length) return;
      const fileIds = result.files.map((f) => f.id);
      this.serviceProviderService
        .addItemMedia(Number(serviceProviderId), productSubKey, fileIds, kind)
        .subscribe({
          next: () =>
            this.messageService.add({
              severity: 'success',
              summary: 'Saved',
              detail: `${fileIds.length} ${kind === 'image' ? 'image' : 'document'}(s) added.`,
            }),
          error: () =>
            this.messageService.add({
              severity: 'error',
              summary: 'Save failed',
              detail: 'The files uploaded but could not be linked. Please try again.',
            }),
        });
    });
  }

  onEditDescription(): void {
    const products = this.products.value;

    if (products && products.length && this.activeProductIndex !== undefined) {
      const product = products[this.activeProductIndex];
      const input = { input: product.description };
      const activeProductIndex = this.activeProductIndex;

      this.clonedProducts[product._meta.id] = _.cloneDeep(product);

      const modalRef = this.modalService.inputTextArea({
        title: 'Product Description',
        buttons: [
          { label: 'Cancel', key: 'cancel', class: 'p-button-secondary' },
          { label: product.description ? 'Update' : 'Save', key: 'save', class: '' },
        ],
        data: input,
      });

      modalRef.onClose.pipe(takeUntil(this.unsubscribe$)).subscribe({
        next: (reason: string) => {
          if (reason && reason.toString().toLowerCase() === 'save') {
            product.description = (input.input || '').trim();

            const existingValue = _.get(this.clonedProducts, [product._meta.id, 'description']);

            if (existingValue !== product.description) {
              this.saveProductField(product, 'description');
            }

            this.updateModifiedProduct(product, activeProductIndex);
          } else {
            this.revertModifiedProduct(product, activeProductIndex);
          }
        },
      });
    }
  }

  onEditNotes(): void {
    const products = this.products.value;

    if (products && products.length && this.activeProductIndex !== undefined) {
      const product = products[this.activeProductIndex];
      const input = { input: product.note };
      const activeProductIndex = this.activeProductIndex;

      this.clonedProducts[product._meta.id] = _.cloneDeep(product);

      const modalRef = this.modalService.inputTextArea({
        title: 'Product Note',
        buttons: [
          { label: 'Cancel', key: 'cancel', class: 'p-button-secondary' },
          { label: product.note ? 'Update' : 'Save', key: 'save', class: '' },
        ],
        data: input,
      });

      modalRef.onClose.pipe(takeUntil(this.unsubscribe$)).subscribe({
        next: (reason: string) => {
          if (reason && reason.toString().toLowerCase() === 'save') {
            product.note = (input.input || '').trim();

            const existingValue = _.get(this.clonedProducts, [product._meta.id, 'note']);

            if (existingValue !== product.note) {
              this.saveProductField(product, 'note');
            }

            this.updateModifiedProduct(product, activeProductIndex);
          } else {
            this.revertModifiedProduct(product, activeProductIndex);
          }
        },
      });
    }
  }

  onEditInvoiceLabel(): void {
    const products = this.products.value;

    if (products && products.length && this.activeProductIndex !== undefined) {
      const product = products[this.activeProductIndex];
      const input = { input: product.invoiceLabel };
      const activeProductIndex = this.activeProductIndex;

      this.clonedProducts[product._meta.id] = _.cloneDeep(product);

      const modalRef = this.modalService.inputTextInput({
        title: 'Invoice Label',
        buttons: [
          { label: 'Cancel', key: 'cancel', class: 'p-button-secondary' },
          { label: product.invoiceLabel ? 'Update' : 'Save', key: 'save', class: '' },
        ],
        data: input,
      });

      modalRef.onClose.pipe(takeUntil(this.unsubscribe$)).subscribe({
        next: (reason: string) => {
          if (reason && reason.toString().toLowerCase() === 'save') {
            product.invoiceLabel = (input.input || '').trim();

            const existingValue = _.get(this.clonedProducts, [product._meta.id, 'invoiceLabel']);

            if (existingValue !== product.invoiceLabel) {
              this.saveProductField(product, 'invoiceLabel');
            }

            this.updateModifiedProduct(product, activeProductIndex);
          } else {
            this.revertModifiedProduct(product, activeProductIndex);
          }
        },
      });
    }
  }

  onEditCategory(index: number | null): void {
    const categories: FormArray = this.service.form.get('productCategories') as FormArray;

    if (categories && categories.length) {
      let categoryFormGroup: FormGroup;
      let categoryValue: any;

      if (index === null) {
        categoryFormGroup = this.generateProductCategoryFormGroup();
        categoryValue = categoryFormGroup.value;
      } else {
        categoryFormGroup = categories.at(index) as FormGroup;
        categoryValue = categoryFormGroup.value;

        this.clonedCategories[categoryValue._meta.id] = _.cloneDeep(categoryValue);
      }

      const modalRef = this.modalService.inputNameValuePair({
        title: 'Product Category',
        buttons: [
          { label: 'Cancel', key: 'cancel', class: 'p-button-secondary' },
          { label: index !== null ? 'Update' : 'Save', key: 'save', class: '' },
        ],
        data: categoryValue,
      });

      modalRef.onClose.pipe(takeUntil(this.unsubscribe$)).subscribe({
        next: (reason: string) => {
          if (reason && reason.toString().toLowerCase() === 'save') {
            this.updateModifiedCategory(categoryValue, index);
          } else {
            if (index !== null) {
              this.revertModifiedCategory(categoryValue, index);
            }
          }
        },
      });
    }
  }

  onDeleteCategory(index: number): void {
    const categories: FormArray = this.service.form.get('productCategories') as FormArray;

    const category = categories.at(index).value;

    const modalRef = this.modalService.generic({
      title: 'Delete Category',
      copy: [`Are you sure you want to delete the '${category?.name}' category?`],
      buttons: [
        { label: 'Cancel', key: 'cancel', class: 'p-button-secondary' },
        { label: 'Delete', key: 'delete', class: '' },
      ],
    });

    modalRef.onClose.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (reason: string) => {
        if (reason.toLowerCase() === 'delete') {
          category.disabled = true;
          this.updateModifiedCategory(category, index);
        }
      },
    });
  }

  onRestoreCategory(index: number): void {
    const categories: FormArray = this.service.form.get('productCategories') as FormArray;

    const category = categories.at(index).value;

    const modalRef = this.modalService.generic({
      title: 'Restore Category',
      copy: [`Are you sure you want to restore the '${category?.name}' category?`],
      buttons: [
        { label: 'Cancel', key: 'cancel', class: 'p-button-secondary' },
        { label: 'Restore', key: 'restore', class: '' },
      ],
    });

    modalRef.onClose.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (reason: string) => {
        if (reason.toLowerCase() === 'restore') {
          category.disabled = false;
          this.updateModifiedCategory(category, index);
        }
      },
    });
  }

  onBespokeProductPricingToggle(event: Event, product: any, index: number): void {
    product.cost.bespoke = !product.cost.bespoke;

    const productsValue: any[] = this.products.value;

    productsValue[index] = product;

    this.products.next(productsValue);

    const formArray = this.service.form.get('products') as FormArray;

    formArray.at(index).patchValue(product);
  }

  upload(event: any) {
    let input = event.files;

    let reader: FileReader = new FileReader();

    reader.readAsText(input[0]);

    reader.onload = (e) => {
      const csv = reader.result as string;

      this.parseCSV(csv);

      event = null;
    };
  }

  private generateForm(formStructure: FormStructure) {
    const formGenerator = new FormGenerator();

    const formData = this.service.form.value;

    // We don't want to include the productCategories and products in the form data because we generate it separately
    // in the `generateCategories` and `generateProducts` methods
    _.unset(formData, 'productCategories');
    _.unset(formData, 'products');

    formGenerator.generate(formStructure, formData, this.service.form);

    this.generateCategories();

    this.generateProducts();
  }

  private revertModifiedProduct(product: any, index: number): void {
    const productsValue: any[] = this.products.value;

    productsValue[index] = this.clonedProducts[product._meta.id];

    this.products.next(productsValue);

    delete this.clonedProducts[product._meta.id];
  }

  private revertModifiedCategory(category: any, index: number): void {
    const categoriesValue: any[] = this.categories.value;

    categoriesValue[index] = this.clonedCategories[category._meta.id];

    this.categories.next(this.formatProductCategoriesForSelectMenu(categoriesValue));

    const formArray = this.service.form.get('productCategories') as FormArray;

    formArray.at(index).patchValue(this.clonedCategories[category._meta.id]);

    delete this.clonedCategories[category._meta.id];
  }

  private updateModifiedProduct(product: any, index: number): void {
    const productsValue: any[] = this.products.value;

    productsValue[index] = product;

    this.products.next(productsValue);

    const formArray = this.service.form.get('products') as FormArray;

    formArray.at(index).patchValue(product);

    delete this.clonedProducts[product._meta.id];
  }

  private updateModifiedCategory(category: any, index: number | null): void {
    const categoriesValue: any[] = this.categories.value;

    if (index === null) {
      categoriesValue.push(category);
    } else {
      categoriesValue[index] = category;
    }

    const formArray = this.service.form.get('productCategories') as FormArray;

    if (index === null) {
      this.onAddCategory(null);

      formArray
        .at(formArray.length - 1)
        .get('name')
        ?.patchValue(category.name);
      formArray
        .at(formArray.length - 1)
        .get('value')
        ?.patchValue(category.value);
    } else {
      formArray.at(index).patchValue(category);
    }

    this.categories.next(this.formatProductCategoriesForSelectMenu(categoriesValue));

    delete this.clonedCategories[category._meta.id];
  }

  private generateCategories(): void {
    const productCategoriesFormArray = this.service.form.get('productCategories') as FormArray;

    // This should never happen, but in the off chance that is does we should deal with it
    if (!productCategoriesFormArray) {
      console.error(`We expected the 'productCategories' FormArray to exist, but it doesn't.`);

      return;
    }

    // If we DON'T have existing categories, create the default categories
    if (!productCategoriesFormArray.length) {
      this.onAddCategory(null);

      productCategoriesFormArray
        .at(0)
        .patchValue({ name: 'Miscellaneous', value: 'miscellaneous' });
    }

    // Now that we have some categories, we need to add them to `this.categories`
    this.categories.next(
      this.formatProductCategoriesForSelectMenu(productCategoriesFormArray.value)
    );
  }

  private generateProducts(): void {
    const productFormArray = this.service.form.get('products') as FormArray;

    // This should never happen, but in the off chance that is does we should deal with it
    if (!productFormArray) {
      console.error(`We expected the 'productFormArray' FormArray to exist, but it doesn't.`);

      return;
    }

    // If we have existing products, restore them
    if (productFormArray.length) {
      this.products.next(productFormArray.value);
    } else {
      // If we DON'T have existing products, create one blank product for the user to populate

      this.onAddProduct(null);
    }
  }

  private generateProductCategoryFormGroup(): FormGroup {
    const uniqueId = new UniqueId();
    const formGenerator = new FormGenerator();
    const dynamicFormStructure = new DynamicFormStructure();

    const productCategoriesForArray = this.service.form.get('productCategories') as FormArray;
    const productCategoriesValue = productCategoriesForArray.value;

    const id = uniqueId.getUniqueInArray(productCategoriesValue, '_meta.id', 'id-');
    const metaIndex = productCategoriesValue.length;

    const formStructure = dynamicFormStructure._getGenericProductCategoryStructure();

    const formGroup = formGenerator.generate(formStructure, {
      _meta: { id, index: metaIndex },
      name: null,
      value: null,
    });

    return formGroup;
  }

  private formatProductCategoriesForSelectMenu(productCategoriesValue: any[]): any[] {
    return _.map(productCategoriesValue, (category) => {
      if (typeof category.value === 'string') {
        return {
          disabled: category.disabled,
          name: category.name,
          value: { name: category.name, value: category.value },
        };
      } else {
        return category;
      }
    });
  }

  private async parseCSV(csv: string): Promise<void> {
    const rowToFormGroup = (csvRow: any, index: number) => {
      const productCategoriesFormArray = this.service.form.get('productCategories') as FormArray;
      const productsFormArray = this.service.form.get('products') as FormArray;

      // Search the existing product categories to see if the category in the csvRow exists
      const getExistingProductCategory = (rowCategory: string): FormGroup | null => {
        let foundCategory: FormGroup | null = null;
        const productCategoriesLength = productCategoriesFormArray.length;

        for (let i = 0; i < productCategoriesLength; i++) {
          const productCategoryFormGroup = productCategoriesFormArray.at(i) as FormGroup;

          const productCategoryValue = productCategoryFormGroup.value;

          const productCategoryKey = productCategoryValue.value;
          const rowCategoryAsKey = stringToKey(rowCategory);

          if (rowCategoryAsKey === productCategoryKey) {
            foundCategory = productCategoryFormGroup;

            break;
          }
        }

        return foundCategory;
      };

      // Search the existing products to see if the product ID in the csvRow exists
      const getExistingProduct = (rowId: string): FormGroup | null => {
        let foundProduct: FormGroup | null = null;

        const productsLength = productsFormArray.length;

        if (!rowId) {
          return foundProduct;
        }

        for (let i = 0; i < productsLength; i++) {
          const productFormGroup = productsFormArray.at(i) as FormGroup;

          const productValue = productFormGroup.value;

          const productId = productValue._meta.id;

          if (rowId.toLowerCase() === productId.toLowerCase()) {
            foundProduct = productFormGroup;

            break;
          }
        }

        return foundProduct;
      };

      // Create a new product category
      const createNewProductCategory = (category: string): void => {
        const newProductCategoryFormGroup = this.generateProductCategoryFormGroup();

        newProductCategoryFormGroup.get('name')?.patchValue(category);
        newProductCategoryFormGroup.get('value')?.patchValue(stringToKey(category));

        productCategoriesFormArray.push(newProductCategoryFormGroup);

        this.categories.value.push(newProductCategoryFormGroup.value);
        this.categories.next(this.formatProductCategoriesForSelectMenu(this.categories.value));
      };

      // Create a new product
      const createNewProduct = (product: any): void => {
        const uniqueId = new UniqueId();
        const formGenerator = new FormGenerator();
        const dynamicFormStructure = new DynamicFormStructure();

        const id = uniqueId.getUniqueInArray(this.products.value, '_meta.id', 'id-');
        const metaIndex = this.nextProductIndex(); // advisory; server assigns on first persist (#215)

        const formStructure = dynamicFormStructure._getGenericProductStructure();

        const categoryIndex = _.findIndex(
          this.categories.value,
          (category) => category.value.value === stringToKey(product.category)
        );

        const formGroup = formGenerator.generate(formStructure, {
          _meta: { id, index: metaIndex },
          name: product.name,
          category: this.categories.value[categoryIndex].value,
          cost: {
            amount: product.cost,
            bespoke: product.bespoke === 1 ? true : false,
          },
          description: product.description,
          note: product.note,
        });

        productsFormArray.push(formGroup);

        this.products.next(productsFormArray.value);
      };

      // Update an existing product
      const updateExistingProduct = (formGroup: FormGroup, product: any): void => {
        const categoryIndex = _.findIndex(
          this.categories.value,
          (category) => category.value.value === stringToKey(product.category)
        );

        formGroup.patchValue(
          _.assign({}, formGroup.value, {
            name: product.name,
            category: this.categories.value[categoryIndex].value,
            cost: {
              amount: product.cost,
              bespoke: product.bespoke === 1 ? true : false,
            },
            description: product.description,
            note: product.note,
          })
        );

        this.products.next(productsFormArray.value);
      };

      let productCategoryFormGroup = getExistingProductCategory(csvRow.category);
      let productFormGroup = getExistingProduct(csvRow.id);

      if (!productCategoryFormGroup) {
        createNewProductCategory(csvRow.category);
      }

      if (!productFormGroup) {
        createNewProduct(csvRow);
      } else {
        updateExistingProduct(productFormGroup, csvRow);
      }
    };

    const rows: any[] = await csvToJson({
      checkType: true,
    }).fromString(csv);

    const rowLength = rows.length;

    for (let i = 0; i < rowLength; i++) {
      rowToFormGroup(rows[i], i);
    }

    if (this.fileUpload) {
      this.fileUpload.clear();
    }
  }
}
